import React from "react";
import { useTranslation } from "react-i18next";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { Search, X, List, Grid3X3 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

interface FilterOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

type ViewMode = "table" | "cards";

interface SearchAndFilterProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  filters?: {
    label: string;
    value: string;
    options: FilterOption[];
    onChange: (value: string) => void;
  }[];
  resultsCount?: number;
  totalCount?: number;
  resultsLabel?: string;
  className?: string;
  /** View mode toggle - if provided, shows list/grid toggle buttons */
  viewMode?: ViewMode;
  /** Callback when view mode changes */
  onViewModeChange?: (mode: ViewMode) => void;
  /** Additional action buttons to render on the right side */
  actions?: React.ReactNode;
}

const SearchAndFilter: React.FC<SearchAndFilterProps> = ({
  searchTerm,
  onSearchChange,
  searchPlaceholder,
  filters = [],
  resultsCount,
  totalCount,
  resultsLabel,
  className = "",
  viewMode,
  onViewModeChange,
  actions,
}) => {
  const { t } = useTranslation();

  const hasActiveFilters = filters.some(
    f => f.value !== "" && f.value !== "all",
  );

  const handleClearFilters = () => {
    onSearchChange("");
    filters.forEach(f => f.onChange(""));
  };

  return (
    <div
      className={`mb-6 bg-white border border-gray-200 rounded-lg ${className}`}
    >
      <div className="px-6 py-3">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
          {/* Left side: Search and Filters */}
          <div className="flex-1 flex flex-col sm:flex-row gap-2 flex-wrap items-center">
            {/* Search Input */}
            <div className="relative w-[150px] lg:w-[250px]">
              <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={searchPlaceholder || t("search")}
                value={searchTerm}
                onChange={e => onSearchChange(e.target.value)}
                className="pl-8 pr-8 h-8"
              />
              {searchTerm && (
                <button
                  onClick={() => onSearchChange("")}
                  className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Filter Dropdowns — Radix Select */}
            {filters.map((filter, index) => (
              <Select
                key={`${index}-${filter.label}`}
                value={filter.value || "__all__"}
                onValueChange={val =>
                  filter.onChange(val === "__all__" ? "" : val)
                }
              >
                <SelectTrigger className="w-[160px] h-8 border-dashed">
                  <SelectValue placeholder={filter.label} />
                </SelectTrigger>
                <SelectContent>
                  {filter.options.map(option => (
                    <SelectItem
                      key={option.value || "__all__"}
                      value={option.value || "__all__"}
                    >
                      <span className="flex items-center gap-2">
                        {option.icon}
                        {option.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ))}

            {/* Reset button when filters are active */}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearFilters}
                className="h-8 px-2 lg:px-3"
              >
                {t("reset", "Reset")}
                <X className="ml-1 h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Right side: Results count, Actions, View Toggle */}
          <div className="flex items-center gap-3">
            {/* Results Count */}
            {(resultsCount !== undefined || totalCount !== undefined) && (
              <div className="flex items-center gap-1 text-sm text-gray-500">
                {resultsCount !== undefined && (
                  <>
                    <span className="font-medium text-gray-900">
                      {resultsCount.toLocaleString()}
                    </span>
                  </>
                )}
                {totalCount !== undefined && resultsCount !== undefined && (
                  <>
                    <span>{t("of")}</span>
                    <span className="font-medium text-gray-900">
                      {totalCount.toLocaleString()}
                    </span>
                  </>
                )}
                {resultsLabel && <span>{resultsLabel}</span>}
              </div>
            )}

            {/* Additional Actions */}
            {actions}

            {/* View Mode Toggle */}
            {viewMode && onViewModeChange && (
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                <Button
                  variant={viewMode === "table" ? "primary" : "ghost"}
                  size="sm"
                  onClick={() => onViewModeChange("table")}
                  className="h-7 px-2.5"
                  title={t("view.table", "Table view")}
                >
                  <List className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === "cards" ? "primary" : "ghost"}
                  size="sm"
                  onClick={() => onViewModeChange("cards")}
                  className="h-7 px-2.5"
                  title={t("view.cards", "Card view")}
                >
                  <Grid3X3 className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchAndFilter;
